// Angular
import { ChangeDetectionStrategy, Component, input } from '@angular/core';

// OLCI
import {select, Store} from '@ngrx/store';
import * as fromRoot from '../../../reducers';
import * as fromAnalytics from '../../../reducers/analytics.reducer';
import { constants } from '../../../constants';

import {TealiumService} from '../services/tealium.service';
import {TelemetryGenericEnum} from '../../../shared/enums';
import {
  TrackClearTelemetryErrorAction,
  TrackInlineAlertEventAction,
  TrackPageErrorEventAction
} from '../../../actions/tracking';
import {take} from 'rxjs/operators';
import {TrackErrorPayload} from '../../../models/action-payloads/tracking-payload';
import { getStatusMessage } from '../../../http-interceptors/http-error-map';
import { HttpErrorResponse } from '@angular/common/http';
import { BookingService } from '../services/booking.service';

@Component({
    selector: 'app-error-wrapper-inner-component',
    templateUrl: 'error-wrapper-inner.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ErrorWrapperInnerComponent {
	public readonly serverErrors = input<HttpErrorResponse | any>();
	public readonly bundle = input<any>();
	errorMessage = '';
  brandName: string;

	constructor(
    private readonly store: Store<fromRoot.AppState>,
    private readonly tealiumService: TealiumService,
    private bookingService: BookingService
  ) {
    this.brandName = this.bookingService.getBrand();
  }

  public get errorMessages(): {
    errorMessage: string;
    errorType: string;
  }[] {
    const errors = [];
    for (const serverError of this.serverErrors()) {
      const error = this.responseErrorMessages(serverError);
      errors.push(error);
    }

    return errors;
  }

	getErrorsByType(errorType: string): any[] {
		return this.errorMessages.filter(e => e.errorType === errorType);
	}

  private responseErrorMessages(serverError: any): {
    errorMessage: string;
    errorType: string;
  } {
    let errorMessage = '';
    let serverErrorHeader = '';
    let serverErrorSubheader = '';
    let serverErrorMessage = '';

    const bundle = this.bundle();
    if (bundle) {
      serverErrorHeader = bundle['server.error.header'];
      serverErrorSubheader = bundle['server.error.subheader'];
      serverErrorMessage = `${serverErrorHeader}. ${serverErrorSubheader}`;
    }

    if (serverError?.status !== constants.HTTP_STATUS_CODE.SUCCESS) {
      errorMessage = getStatusMessage(serverError?.error?.error?.errorCode, bundle);
    }

    this.store
      .pipe(
        select(fromRoot.getAnalyticsState),
        take(1)
      )
      .subscribe(data => {
        if (data) {
          this.dispatchBackendErrorInteraction(data);
        } else {
          this.dispatchAnalyticsAlertEvent(serverErrorMessage);
        }
      });

    return {
      errorMessage: errorMessage || serverErrorMessage,
      errorType: 'warning'
    };
  }

  private dispatchBackendErrorInteraction(data: fromAnalytics.State) {
    const telemetryData: TrackErrorPayload = this.tealiumService.retrieveAnalyticsBackendErrorInteraction(data);
    setTimeout(() => {
      this.store.dispatch(new TrackPageErrorEventAction(telemetryData));
      this.store.dispatch(new TrackClearTelemetryErrorAction());
    });
  }

  private dispatchAnalyticsAlertEvent(serverErrorMessage: string) {
    const telemetryData = this.tealiumService.retrieveAnalyticsAlertInlineResponse(
      TelemetryGenericEnum.EMPTY_VALUE,
      TelemetryGenericEnum.INLINE_ERROR,
      serverErrorMessage,
      true
    );
    setTimeout(() => {
      this.store.dispatch(new TrackInlineAlertEventAction(telemetryData));
    });
  }
}
